import { ConfigurableParameterDto, ParameterTypes } from "api/pricing"
import { InputWithUnit } from "components/forms/fragments/InputWithUnit"
import { Sizes } from "../ui/Loader"
import { FunnelCardWarning } from "components/display/FunnelCardWarning"
import { FC, useCallback, useEffect, useState } from "react"
import { GenericNumberSelector } from "./GenericNumberSelector"

interface GenericParameterProps {
  title: string
  subtitle?: string
  value: number
  size?: Sizes.sm | Sizes.md
  onChange: React.Dispatch<React.SetStateAction<number>>
  parameters?: ConfigurableParameterDto
  disabledUnit?: boolean
  warningMessage?: string
  placeholder?: string
}

export const GenericParameter: FC<GenericParameterProps> = ({
  title,
  subtitle,
  value,
  size = Sizes.md,
  onChange,
  parameters,
  disabledUnit = false,
  warningMessage,
  placeholder
}) => {
  // State local pour gérer les ajustements automatiques
  const [localValue, setLocalValue] = useState(value);
  // Brouillon de saisie : la frappe est LIBRE, la contrainte (min/max ou liste de
  // valeurs) ne s'applique qu'au blur — clamper à chaque frappe rendait la saisie
  // impossible (taper « 150 » passait par 1, 15… snapés immédiatement).
  const [draft, setDraft] = useState(value.toString());

  // Ramène une valeur dans les contraintes du référentiel (RANGE ou liste de choix).
  const clampToParameters = useCallback(
    (raw: number): number => {
      if (!parameters?.values?.length || isNaN(raw)) return isNaN(raw) ? value : raw;
      switch (parameters.type) {
        case ParameterTypes.NUMBER_1: { // RANGE
          if (parameters.values.length < 2) return raw;
          const min = Math.min(...parameters.values);
          const max = Math.max(...parameters.values);
          // A degenerate range (min === max, e.g. [0, 0] sent for a fee without bounds)
          // carries no real constraint: never clamp against it.
          if (min === max) return raw;
          return Math.min(max, Math.max(min, raw));
        }
        case ParameterTypes.NUMBER_2: // VALUES (liste de choix) : la plus proche
          return parameters.values.reduce((prev, curr) =>
            Math.abs(curr - raw) < Math.abs(prev - raw) ? curr : prev
          );
        default:
          return raw;
      }
    },
    [parameters, value]
  );

  // Synchroniser le state local avec la prop value (changement externe)
  useEffect(() => {
    setLocalValue(value);
    setDraft(value.toString());
  }, [value]);

  // Valeur entrante hors contraintes (ex. config chargée avec un ancien référentiel) :
  // corrigée UNE fois au chargement/changement de paramètres — jamais pendant la frappe.
  useEffect(() => {
    const adjusted = clampToParameters(value);
    if (adjusted !== value) {
      setLocalValue(adjusted);
      setDraft(adjusted.toString());
      onChange(adjusted);
    }
    // volontairement PAS localValue/draft en dépendances (le blur gère la saisie)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameters, value]);

  // Gestion des paramètres selon le type
  const getInputProps = () => {
    if (!parameters) return {};
    switch (parameters.type) {
      case ParameterTypes.NUMBER_1: // RANGE
        if (
          parameters.values &&
          parameters.values.length >= 2 &&
          Math.min(...parameters.values) !== Math.max(...parameters.values)
        ) {
          return {
            min: Math.min(...parameters.values),
            max: Math.max(...parameters.values),
            unit: disabledUnit ? undefined : parameters.unit,
            step: 1
          };
        }
        return {};
      case ParameterTypes.NUMBER_2: // VALUES (liste de choix)
        return {
          allowedValues: parameters.values,
          unit: disabledUnit ? undefined : parameters.unit,
        };
      default:
        return {};
    }
  };

  // Changement commité (sélecteur à choix, ou blur de l'input) : clamp + dispatch.
  const handleManualChange = (newValue: React.SetStateAction<number>) => {
    const resolvedValue = typeof newValue === 'function' ? newValue(localValue) : newValue;
    const adjusted = clampToParameters(resolvedValue);
    setLocalValue(adjusted);
    setDraft(adjusted.toString());
    onChange(adjusted);
  };

  // Fin de saisie clavier : on valide (clamp) et on notifie le parent.
  const commitDraft = () => {
    handleManualChange(Number(draft));
  };

  // Pour le type VALUES, on peut créer un select ou adapter le NumberSelector
  const renderInput = () => {
    const inputProps = getInputProps();
    if (parameters?.type === ParameterTypes.NUMBER_2)
      return (
        <GenericNumberSelector
          value={localValue}
          variant={size}
          onChange={handleManualChange}
          id={`selector-${title.replace(/\s+/g, '-').toLowerCase()}`}
          values={parameters?.type === ParameterTypes.NUMBER_2 ? parameters.values : undefined}
          {...inputProps}
        />
      );

    return <InputWithUnit
      placeholder={placeholder}
      variant="guarantee"
      unit={parameters && parameters.unit ? parameters.unit : 'NaN'}
      // Même largeur que le sélecteur à choix (cohérence visuelle) et de la place
      // pour les gros montants (plafonds à 7 chiffres)
      variantSize={Sizes.base}
      value={draft}
      warningMessage={warningMessage}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commitDraft}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
    />
  };

  return (
    <div className={"flex flex-col justify-center gap-2"}>
      <div className="flex flex-col w-full">
        <div className="flex items-center gap-1">
          <p className={"text-sm"}>
            {title}
          </p>
          {warningMessage && (
            <FunnelCardWarning warningMessages={[warningMessage]} />
          )}
        </div>
        {subtitle && (
          <p className="text-xs font-light">
            {subtitle}
          </p>
        )}
      </div>
      {renderInput()}
    </div>
  )
}
